import type { HTMLAttributes, ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Form section group used in Step 2 (Podaci o projektu) — semantic
 * grouping with a title bar matching the legacy ``.section-group`` /
 * ``.section-group-title`` style.
 */
export function Section({
  title,
  children,
  className,
  action,
  ...props
}: { title: string; action?: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "mt-6 first:mt-0 border-t border-stone-100 pt-5 first:border-t-0 first:pt-0",
        className,
      )}
      {...props}
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-[0.78rem] font-bold uppercase tracking-[0.05em] text-stone-500">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

/**
 * Field-group helper — label + control + optional hint, vertical
 * stack. Mirrors the legacy ``.form-group`` shape but as a component
 * so react-hook-form ``register()`` plugs in cleanly.
 */
export function FieldGroup({
  className,
  children,
  full,
}: {
  className?: string;
  children: ReactNode;
  full?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex flex-col",
        full && "col-span-full",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function FormGrid({
  cols = 2,
  className,
  children,
}: {
  cols?: 1 | 2 | 3;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "grid gap-3.5",
        cols === 1 && "grid-cols-1",
        cols === 2 && "grid-cols-1 md:grid-cols-2",
        cols === 3 && "grid-cols-1 md:grid-cols-3",
        className,
      )}
    >
      {children}
    </div>
  );
}
